import { Div, Text } from "atomize"
import { useContext, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import { theme } from "../settings/config"
import { lessonsContext } from "../providers/lessons.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { getLessonColor, getLessonTypeLabel } from "../utils/func.utils"
import { resolveLessonTypes } from "../utils/school.utils"
import { withMatchLessonType } from "../utils/match.utils"
import { formatMinutes, lessonEndMinutes, lessonStartMinutes } from "../utils/week.utils"
import DashedOutline, { DASH } from "../atoms/DashedOutline"
import SchoolAvatar from "../school/SchoolAvatar"

// Le lezioni del maestro negli altri circoli hanno il colore della loro
// tipologia, come quelle di qui, ma attenuato e a tratteggio: si vede subito
// che lezione è, e che non è di qui. Il tondo col logo dice dove.
export const ELSEWHERE_TINT_OPACITY = 0.55
export const ELSEWHERE_OUTLINE_OPACITY = 0.5

// Il nome del circolo di una lezione, per chi la guarda da un altro
export const useSchoolName = () => {
	const { schools } = useContext(schoolContext)
	return schoolID => schools[schoolID]?.schoolName ?? "Altro circolo"
}

// La tipologia di una lezione com'è nel suo circolo: quelle del catalogo
// sono uguali ovunque, ma una tipologia propria il circolo la chiama e la
// colora come vuole, e qui va vista come la vede lui.
export const useElsewhereLessonType = () => {
	const { catalogLessonTypes } = useContext(sharedDataContext)
	const { schools } = useContext(schoolContext)
	return lesson =>
		withMatchLessonType(
			resolveLessonTypes(catalogLessonTypes, schools[lesson.schoolID]?.lessonTypes),
		)[lesson.lessonType] ?? null
}

// Dalla lezione altrove al suo circolo, sul suo giorno, con la lezione già
// aperta: è lì che si modifica.
export const useGoToElsewhereLesson = () => {
	const { setSelectedSchool } = useContext(schoolContext)
	const { setSelectedDate, setLessonsView, setLessonToOpen } = useContext(lessonsContext)

	return lesson => {
		setLessonToOpen(`${lesson.coachID}_${lesson.lessonID}`)
		setSelectedDate(DateTime.fromJSDate(lesson.lessonStart.toDate()).toISODate())
		setSelectedSchool(lesson.schoolID)
		setLessonsView("day")
	}
}

// Gli angoli di una riga: tondi, e quasi dritti dove è attaccata a quella
// prima o a quella dopo ("Gioco io" di seguito a una lezione, vedi MatchRow).
// Le lezioni altrove di seguito invece stanno in un blocco solo, tondo ai capi.
const ROUND = 15
const JOINED = 5
export const cornersOf = ({ joinsPrev, joinsNext } = {}) => {
	const top = joinsPrev ? JOINED : ROUND
	const bottom = joinsNext ? JOINED : ROUND
	return [top, top, bottom, bottom]
}

// Il filo tra due lezioni dello stesso blocco: tratteggiato come il bordo,
// ma più leggero, perché divide e non chiude
const ELSEWHERE_DIVIDER_OPACITY = 0.3

function Divider({ color }) {
	return (
		<svg
			aria-hidden="true"
			focusable="false"
			style={{ display: "block", width: "100%", height: 1, overflow: "visible" }}>
			<line
				x1="0"
				y1="0.5"
				x2="100%"
				y2="0.5"
				style={{
					stroke: color,
					strokeWidth: 1,
					strokeDasharray: DASH,
					strokeOpacity: ELSEWHERE_DIVIDER_OPACITY,
				}}
			/>
		</svg>
	)
}

// Le lezioni che il maestro ha in un altro circolo, nell'elenco del giorno e
// nell'agenda della settimana: una riga per lezione, uguale nei due posti,
// col tondo del circolo dove le lezioni di qui hanno il campo. Quelle di
// seguito nello stesso circolo (`groupLessonsElsewhere`) stanno in un blocco
// solo, un tratteggio intorno a tutte e un filo tra una e l'altra. Toccando
// una riga si apre quanto basta per andarci.
//
// `paddingX` è dove cominciano le scritte, lo stesso delle schede accanto
// (16px nel giorno, 12 nella settimana): un numero, o un MotionValue che
// segue il passaggio da una vista all'altra.
function ElsewhereLessonRow({ lessons, m, paddingX = 16 }) {
	const lessonTypeOf = useElsewhereLessonType()
	const colors = lessons.map(lesson => getLessonColor(lessonTypeOf(lesson), true))
	// il bordo ha il colore delle lezioni che chiude; se sono di tipologie
	// diverse, quello neutro
	const outline = colors.every(color => color === colors[0]) ? colors[0] : theme.colors.gunmetal

	return (
		<Div m={m} pos="relative" data-testid="elsewhere-block">
			{lessons.map((lesson, i) => (
				<ElsewhereLine
					key={`${lesson.schoolID}_${lesson.lessonID}`}
					lesson={lesson}
					isFirst={i === 0}
					isLast={i === lessons.length - 1}
					dividerColor={outline}
					paddingX={paddingX}
				/>
			))}
			<DashedOutline color={outline} radius={ROUND} opacity={ELSEWHERE_OUTLINE_OPACITY} />
		</Div>
	)
}

function ElsewhereLine({ lesson, isFirst, isLast, dividerColor, paddingX }) {
	const { schools } = useContext(schoolContext)
	const lessonTypeOf = useElsewhereLessonType()
	const schoolName = useSchoolName()
	const goToLesson = useGoToElsewhereLesson()
	const [isOpen, setOpen] = useState(false)

	const school = schoolName(lesson.schoolID)
	const lessonType = lessonTypeOf(lesson)
	const darkColor = getLessonColor(lessonType, true)
	const time = `${formatMinutes(lessonStartMinutes(lesson))}–${formatMinutes(lessonEndMinutes(lesson))}`
	// il colore della riga arriva al bordo del blocco: tondo solo ai capi
	const top = isFirst ? ROUND : 0
	const bottom = isLast ? ROUND : 0
	const borderRadius = `${top}px ${top}px ${bottom}px ${bottom}px`
	const sides = { paddingLeft: paddingX, paddingRight: paddingX }

	return (
		<Div
			pos="relative"
			// il colore attenuato sta sotto alle scritte, non sopra
			style={{ isolation: "isolate", borderRadius }}
			data-testid="elsewhere-lesson">
			<Div
				pos="absolute"
				bg={getLessonColor(lessonType)}
				style={{ inset: 0, zIndex: -1, borderRadius, opacity: ELSEWHERE_TINT_OPACITY }}
			/>
			{!isFirst && (
				<motion.div style={sides}>
					<Divider color={dividerColor} />
				</motion.div>
			)}
			<motion.button
				type="button"
				aria-expanded={isOpen}
				aria-label={`${time}, lezione al ${school}`}
				style={{
					...sides,
					width: "100%",
					display: "flex",
					alignItems: "center",
					gap: "0.75rem",
					paddingTop: "0.45rem",
					paddingBottom: "0.45rem",
					margin: 0,
					background: "transparent",
					border: 0,
					borderRadius,
					cursor: "pointer",
					textAlign: "left",
					font: "inherit",
				}}
				onClick={() => setOpen(!isOpen)}>
				<Text
					textWeight="bold"
					textSize="caption"
					textColor={theme.colors.gunmetal}
					style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
					{time}
				</Text>
				<Div flexGrow="1" style={{ minWidth: 0 }}>
					<Text
						textSize="caption"
						textWeight="600"
						textColor={theme.colors.gunmetal}
						style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
						{school}
					</Text>
					<Text textSize="tiny" textColor={darkColor}>
						{getLessonTypeLabel(lesson.lessonType, lessonType)}
					</Text>
				</Div>
				<SchoolAvatar school={schools[lesson.schoolID]} size={28} tone="onTint" />
			</motion.button>

			<AnimatePresence initial={false}>
				{isOpen && (
					<motion.div
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ ease: "easeInOut", duration: 0.2 }}
						style={{ overflow: "hidden" }}>
						<motion.div
							style={{
								...sides,
								paddingBottom: "0.6rem",
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: "0.75rem",
							}}>
							<Text textSize="tiny" textColor={theme.colors.text_light}>
								Si modifica dal suo circolo
							</Text>
							<Div
								tag="button"
								type="button"
								d="flex"
								align="center"
								h="2.25rem"
								p={{ x: "0.75rem" }}
								rounded="12px"
								border="0"
								cursor="pointer"
								bg={theme.colors.surface}
								textColor={theme.colors.gunmetal}
								textWeight="600"
								style={{ gap: "0.4rem", flexShrink: 0, font: "inherit", fontSize: "0.85rem" }}
								onClick={() => goToLesson(lesson)}>
								Vai al circolo
								<FontAwesomeIcon icon={faArrowRight} size="sm" />
							</Div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</Div>
	)
}

export default ElsewhereLessonRow
